import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { SectionTitle } from "@/components/dashboard/widgets";
import {
  Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription,
} from "@/components/ui/sheet";
import { Loader2, Store, CheckCircle2, XCircle, Eye } from "lucide-react";
import { toast } from "sonner";

/**
 * Admin — Lander Template Marketplace moderation queue.
 * Approve / reject publisher-submitted templates; feature rank controls
 * surface order in the publisher-facing /publisher/marketplace catalogue.
 */
export default function MarketplaceModeration() {
  const [status, setStatus] = useState("pending");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [active, setActive] = useState(null);

  const load = () => {
    setLoading(true);
    api.get(`/admin/marketplace?status=${status}`)
      .then((r) => setRows(r.data || []))
      .finally(() => setLoading(false));
  };
  useEffect(load, [status]);

  return (
    <div className="space-y-6" data-testid="admin-marketplace">
      <SectionTitle
        eyebrow="Marketplace"
        title="Template moderation"
        subtitle="Review publisher-submitted lander templates. Approve to publish in the catalogue — authors earn 10% revenue share on every accepted lead."
      />

      <div className="flex items-center gap-2 flex-wrap">
        {["pending", "approved", "rejected", "all"].map((s) => (
          <button
            key={s}
            onClick={() => setStatus(s)}
            data-testid={`mkt-mod-filter-${s}`}
            className={`px-3 py-1 text-[12px] rounded-full capitalize ${status === s ? "bg-slate-900 text-white" : "border border-slate-200 text-slate-600 hover:bg-slate-50"}`}
          >
            {s}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="grid place-items-center py-20"><Loader2 className="size-5 text-slate-400 animate-spin" /></div>
      ) : rows.length === 0 ? (
        <Card className="rounded-xl border-dashed border-slate-300 bg-slate-50/40">
          <CardContent className="p-10 text-center text-slate-500 text-sm">No {status} templates.</CardContent>
        </Card>
      ) : (
        <div className="grid gap-3">
          {rows.map((t) => (
            <Card key={t.id} className="rounded-xl border-slate-200 shadow-soft" data-testid={`mkt-mod-row-${t.id}`}>
              <CardContent className="p-5">
                <div className="flex items-center justify-between gap-3 flex-wrap">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <h3 className="font-semibold text-slate-900">{t.name}</h3>
                      <span className="text-[10.5px] uppercase tracking-widest text-slate-500">{t.sector?.replace("-", " ")}</span>
                      <span
                        className="text-[10.5px] uppercase tracking-widest font-semibold px-2 py-0.5 rounded"
                        style={{
                          background: t.status === "approved" ? "#ecfdf5" : t.status === "rejected" ? "#fef2f2" : "#fefce8",
                          color:      t.status === "approved" ? "#047857" : t.status === "rejected" ? "#b91c1c" : "#a16207",
                        }}
                      >{t.status}</span>
                    </div>
                    <p className="text-[12.5px] text-slate-500 mt-1 line-clamp-1">{t.description || t.subheadline}</p>
                    <div className="text-[11.5px] text-slate-500 mt-1">
                      By {t.author_company} · {(t.uses || 0)} uses · £{(t.earnings_credited || 0).toFixed(2)} credited
                    </div>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <Button variant="outline" size="sm" className="rounded-full" onClick={() => setActive(t.id)} data-testid={`mkt-mod-review-${t.id}`}>
                      <Eye className="size-3.5" /> Review
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <ReviewSheet id={active} onClose={() => { setActive(null); load(); }} />
    </div>
  );
}

function ReviewSheet({ id, onClose }) {
  const [data, setData] = useState(null);
  const [rank, setRank] = useState(0);
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(null);

  useEffect(() => {
    if (!id) { setData(null); setNote(""); return; }
    // No detail endpoint, fetch by id via admin list with status=all
    api.get(`/admin/marketplace?status=all`).then((r) => {
      const t = (r.data || []).find((x) => x.id === id);
      setData(t || null);
      setRank(t?.feature_rank || 0);
    });
  }, [id]);

  const decide = async (action) => {
    setBusy(action);
    try {
      await api.post(`/admin/marketplace/${id}/${action}`, {
        feature_rank: action === "approve" ? rank : undefined,
        note: note.trim() || undefined,
      });
      toast.success(action === "approve" ? "Template approved & published" : "Template rejected");
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Action failed");
    } finally { setBusy(null); }
  };

  if (!id) return null;
  return (
    <Sheet open={!!id} onOpenChange={(o) => !o && onClose()}>
      <SheetContent className="w-[640px] sm:max-w-[640px] overflow-y-auto" data-testid="mkt-mod-sheet">
        <SheetHeader>
          <SheetTitle className="flex items-center gap-2"><Store className="size-5 text-[#0055FF]" /> {data?.name || "Loading…"}</SheetTitle>
          <SheetDescription>By {data?.author_company || "—"} · {data?.sector?.replace("-", " ")}</SheetDescription>
        </SheetHeader>
        {!data ? <div className="py-10 grid place-items-center"><Loader2 className="size-5 text-slate-400 animate-spin" /></div> : (
          <div className="space-y-5 mt-4">
            {data.description && (
              <Card className="rounded-lg border-slate-200">
                <CardContent className="p-4 text-[13.5px] text-slate-700 leading-relaxed">{data.description}</CardContent>
              </Card>
            )}
            <div className="grid grid-cols-2 gap-4">
              <Mini label="Headline"     value={data.headline} />
              <Mini label="CTA"          value={data.cta_label} />
              <Mini label="Sub-headline" value={data.subheadline || "—"} />
              <Mini label="Eyebrow"      value={data.hero_eyebrow || "—"} />
            </div>
            <div className="grid grid-cols-2 gap-4 text-[12.5px]">
              <Mini label="Uses to date"      value={data.uses || 0} />
              <Mini label="Leads attributed"  value={data.leads_attributed || 0} />
              <Mini label="Earnings credited" value={`£${(data.earnings_credited || 0).toFixed(2)}`} />
              <Mini label="Status"            value={<span className="capitalize">{data.status}</span>} />
            </div>
            <div className="space-y-1.5">
              <Label>Feature rank (higher = surfaced first in publisher list)</Label>
              <Input type="number" min={0} max={100} value={rank} onChange={(e) => setRank(parseInt(e.target.value || "0"))} data-testid="mkt-mod-rank" />
            </div>
            <div className="space-y-1.5">
              <Label>Moderator note (optional)</Label>
              <Textarea rows={3} value={note} onChange={(e) => setNote(e.target.value)} placeholder="Feedback shown to the author / internal audit trail" data-testid="mkt-mod-note" />
            </div>
            <div className="flex items-center gap-2 pt-2">
              <Button onClick={() => decide("approve")} disabled={busy} className="rounded-full bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="mkt-mod-approve">
                {busy === "approve" ? <Loader2 className="size-4 animate-spin" /> : <CheckCircle2 className="size-4" />} Approve
              </Button>
              <Button onClick={() => decide("reject")} disabled={busy} variant="outline" className="rounded-full text-rose-600 border-rose-200 hover:bg-rose-50" data-testid="mkt-mod-reject">
                {busy === "reject" ? <Loader2 className="size-4 animate-spin" /> : <XCircle className="size-4" />} Reject
              </Button>
            </div>
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}

function Mini({ label, value }) {
  return (
    <div>
      <div className="text-[10.5px] uppercase tracking-widest text-slate-500">{label}</div>
      <div className="text-[14px] text-slate-900 mt-0.5">{value}</div>
    </div>
  );
}
